import * as React from 'react';

import classnames from 'classnames';
import {motion} from 'framer-motion';

import {colors} from '../colors/colors';

import styles from './switch.module.css';

type Props = {
    isOn: boolean;
    onChange: (e: React.SyntheticEvent<HTMLDivElement>) => void;
    isDisabled?: boolean;
    isRounded?: boolean;
    shouldAnimate?: boolean;
    isSmall?: boolean;
    // If true, the onChange event will not bubble up to the parent
    shouldStopPropagation?: boolean;
    tooltipText?: string;
};

const containerVariants = {
    active: {
        backgroundColor: colors.blue,
        color: '#ffffff',
        borderColor: colors.blue,
    },
    inactive: {
        backgroundColor: colors.offWhite,
        color: colors.grey,
        borderColor: colors.offWhiteDk,
    },
} as const;

// @ts-expect-error
const getSliderVariants = (isRounded, isSmall) => {
    let activeLeft;
    if (isSmall) {
        activeLeft = isRounded ? 18 : 20;
    } else {
        activeLeft = isRounded ? 20 : 59;
    }

    const baseVariants = {
        active: {
            left: activeLeft,
            width: isRounded && (isSmall ? 13 : 18),
            height: isRounded && (isSmall ? 13 : 18),
            top: isRounded && (isSmall ? 2 : 1),
            backgroundColor: isRounded && colors.white,
        },
        inactive: {
            left: isRounded ? 4 : 6,
            backgroundColor: isRounded ? colors.formBorder : colors.white,
            width: isRounded && (isSmall ? 12 : 12),
            height: isRounded && (isSmall ? 11 : 11),
            top: isRounded && (isSmall ? 2 : 4),
        },
    } as const;

    return baseVariants;
};

/**
 * Use this to indicate to a user if a setting is on or off,
 * and allow them to change the value. Its basically a really fancy checkbox.
 */
export function Switch(props: Props) {
    const {
        isOn,
        isDisabled,
        isRounded,
        isSmall,
        onChange,
        shouldAnimate = true,
        shouldStopPropagation = false,
    } = props;

    // Track if component has mounted to prevent initial animation
    const [hasMounted, setHasMounted] = React.useState(false);

    React.useEffect(() => {
        setHasMounted(true);
    }, []);

    const styleNames = classnames({
        [styles['container']]: true,
        [styles['container--disabled']]: isDisabled,
        [styles['container--disabled-inactive']]: isDisabled && !isOn,
        [styles['container--round']]: isRounded,
        [styles['container--small']]: isSmall,
    });

    // Get the current styles based on state (disabled is independent of active/inactive)
    const variantKey = isOn ? 'active' : 'inactive';
    const currentContainerStyle = containerVariants[variantKey];
    const sliderVariants = getSliderVariants(isRounded, isSmall);
    const currentSliderStyle = sliderVariants[variantKey];

    const handleClick = (e: React.SyntheticEvent<HTMLDivElement>) => {
        if (isDisabled) {
            return;
        }

        if (shouldStopPropagation) {
            e.stopPropagation();
        }

        onChange(e);
    };

    return shouldAnimate ? (
        <motion.div
            onClick={handleClick}
            disabled={isDisabled}
            // @ts-expect-error
            animate={hasMounted ? variantKey : false}
            variants={containerVariants}
            initial={variantKey}
            data-tip={props.tooltipText}
            tabIndex={isDisabled ? -1 : 0}
            className={styleNames}
        >
            <div className={styles['inner-container']}>
                {!isRounded ? (
                    <>
                        <div className={styles['label']}>On</div>
                        <div className={styles['label']}>Off</div>
                    </>
                ) : (
                    <div style={{width: isSmall ? '33px' : '40px'}} />
                )}
            </div>
            <motion.div
                // @ts-expect-error
                animate={hasMounted ? variantKey : false}
                variants={sliderVariants}
                initial={variantKey}
                transition={{type: 'tween', ease: 'easeInOut', duration: 0.2}}
                className={isRounded ? styles['slider--round'] : styles['slider']}
            />
        </motion.div>
    ) : (
        <div
            onClick={handleClick}
            style={currentContainerStyle}
            data-tip={props.tooltipText}
            className={styleNames}
        >
            <div className={styles['inner-container']}>
                {!isRounded ? (
                    <>
                        <div className={styles['label']}>On</div>
                        <div className={styles['label']}>Off</div>
                    </>
                ) : (
                    <div style={{width: isSmall ? '33px' : '40px'}} />
                )}
            </div>
            <div
                style={currentSliderStyle}
                className={isRounded ? styles['slider--round'] : styles['slider']}
            />
        </div>
    );
}
